import { useEffect, useState } from "react";
import { Box, Text, Button as ThemeUiButton } from "theme-ui";
import { motion, AnimatePresence } from "framer-motion";
import OutsideClickHandler from "react-outside-click-handler";

import { useTranslation } from "hooks";
import * as styles from "./ImagesButton.styles";

export default function ImagesButton({
  selectedImageViewAngle: parentSelectedImageAngle,
  imageViewAnglesObj,
  setSelectedImageAngle: parentSetSelectedImageAngle,
}) {
  const MENU_STATES = {
    CLOSE_COMPLETE: "CLOSE_COMPLETE",
    OPEN_START: "OPEN_START",
    OPEN_COMPLETE: "OPEN_COMPLETE",
    CLOSE_START: "CLOSE_START",
  };

  const { t } = useTranslation();

  const [selectedImageAngle, setSelectedImageAngle] = useState(
    parentSelectedImageAngle
  );

  const [menuState, setMenuState] = useState(MENU_STATES.CLOSE_COMPLETE);

  const openMenu = () => setMenuState(MENU_STATES.OPEN_START);

  const closeMenu = () => setMenuState(MENU_STATES.CLOSE_START);

  const isChangeViewMenuOpening = [
    MENU_STATES.OPEN_START,
    MENU_STATES.OPEN_COMPLETE,
  ].includes(menuState);

  const toggleMenu = () => {
    if (isChangeViewMenuOpening) {
      closeMenu();
    } else {
      openMenu();
    }
  };

  useEffect(() => {
    if (selectedImageAngle !== parentSelectedImageAngle) {
      setMenuState(MENU_STATES.CLOSE_START);
    }
  }, [parentSelectedImageAngle, selectedImageAngle]);

  const imageViewAngles = Object.keys(imageViewAnglesObj).filter(
    (imageViewAngle) => Boolean(imageViewAnglesObj[imageViewAngle])
  );

  const displayedImageViewAngles =
    menuState === MENU_STATES.CLOSE_START
      ? []
      : [
          imageViewAngles.find(
            (imageViewAngle) => imageViewAngle === selectedImageAngle
          ),
          ...imageViewAngles.filter(
            (imageViewAngle) =>
              imageViewAngle !== selectedImageAngle && isChangeViewMenuOpening
          ),
        ];

  return (
    <OutsideClickHandler onOutsideClick={closeMenu}>
      <Box sx={styles.imagesContainer}>
        <AnimatePresence initial={false} key={selectedImageAngle}>
          {displayedImageViewAngles.map((imageViewAngle, index) => {
            const { url, alt } = imageViewAnglesObj[imageViewAngle];

            const initial = { opacity: 0, scale: 0 };

            const isLast = index + 1 === displayedImageViewAngles.length;

            const animate = {
              opacity: 1,
              scale: 1,
              transition: {
                duration: 0.2,
                delay: index * 0.1,
              },
            };

            const exit = {
              ...(imageViewAngle === parentSelectedImageAngle
                ? {
                    x: -1 * (index * 90),
                  }
                : initial),
              transition: {
                duration: 0.4,
              },
            };

            return (
              <motion.div
                key={imageViewAngle}
                initial={index > 0 ? initial : { opacity: 1, scale: 1 }}
                animate={animate}
                exit={exit}
                onAnimationComplete={
                  isLast &&
                  (() => {
                    if (menuState === MENU_STATES.OPEN_START) {
                      setMenuState(MENU_STATES.OPEN_COMPLETE);
                    } else {
                      setMenuState(MENU_STATES.CLOSE_COMPLETE);
                      setSelectedImageAngle(parentSelectedImageAngle);
                    }
                  })
                }
              >
                <ThemeUiButton
                  variant="unstyled"
                  sx={styles.imagesButton}
                  aria-label={`${alt} view`}
                  onClick={() => {
                    if (index === 0) {
                      toggleMenu();
                    }
                    parentSetSelectedImageAngle(imageViewAngle);
                  }}
                >
                  <Box
                    sx={styles.image({
                      backgroundImageUrl: url,
                      isSelected: selectedImageAngle === imageViewAngle,
                    })}
                  />

                  {menuState === MENU_STATES.CLOSE_COMPLETE && (
                    <Text sx={styles.changeViewIcon}>{t("Change View")}</Text>
                  )}
                </ThemeUiButton>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>
    </OutsideClickHandler>
  );
}
